import type {
    ActiveStripeProduct,
    StripePaymentInfo,
    StripeProduct,
    UpcomingStripeProduct,
    UserGrant,
} from '~/@types/backend-types'

import type { Action } from '../common/actions'

import {
    PaymentInfoFetched,
    PlanChangeFailed,
    PlanChangeStarted,
    PlanChangeSucceeded,
    PurchaseFailed,
    PurchaseStarted,
    PurchaseSucceeded,
    StorageProductsFetched,
    FetchStripeSubscriptionInfoSucceeded,
    UserStoragePlanFetched,
    FetchStripeSubscriptionInfoFailed,
    PlanChangeActionRequired,
    PlanChangeActionRequiredHandled,
} from './actions'

export type StripeSubscriptionInfo = {
    subscriptionId: string
    activeProduct?: ActiveStripeProduct
    upcomingProduct?: UpcomingStripeProduct
}

export type StorageState = {
    availableProducts: StripeProduct[]
    availableTrial: number
    userGrants: UserGrant[]
    paymentInfo: StripePaymentInfo | null
    stripeSubscriptionInfo: StripeSubscriptionInfo | null
    isUpdatingStoragePlan: boolean
    actionRequiredSubscriptionId: string | null
}

const initialState = {
    availableProducts: [],
    availableTrial: 0,
    userGrants: [],
    paymentInfo: null,
    stripeSubscriptionInfo: null,
    isUpdatingStoragePlan: false,
    actionRequiredSubscriptionId: null,
}

export const storagePlanReducer = (
    state: StorageState = initialState,
    action: Action,
): StorageState => {
    if (StorageProductsFetched.match(action)) {
        return {
            ...state,
            availableProducts: action.payload.result,
            availableTrial: action.payload.trial_period_months,
        }
    }

    if (UserStoragePlanFetched.match(action)) {
        return {
            ...state,
            userGrants: action.payload,
        }
    }
    if (PaymentInfoFetched.match(action)) {
        return {
            ...state,
            paymentInfo: action.payload,
        }
    }

    if (PurchaseStarted.match(action) || PlanChangeStarted.match(action)) {
        return {
            ...state,
            isUpdatingStoragePlan: true,
            actionRequiredSubscriptionId: null,
        }
    }
    if (
        PurchaseSucceeded.match(action) ||
        PurchaseFailed.match(action) ||
        PlanChangeSucceeded.match(action) ||
        PlanChangeFailed.match(action)
    ) {
        return {
            ...state,
            isUpdatingStoragePlan: false,
            actionRequiredSubscriptionId: null,
        }
    }

    if (PlanChangeActionRequired.match(action)) {
        return {
            ...state,
            isUpdatingStoragePlan: false,
            actionRequiredSubscriptionId: action.payload,
        }
    }

    if (PlanChangeActionRequiredHandled.match(action)) {
        return {
            ...state,
            actionRequiredSubscriptionId: null,
        }
    }

    if (FetchStripeSubscriptionInfoSucceeded.match(action)) {
        const { subscription_id, products } = action.payload
        const activeProduct = products.find(
            (s): s is ActiveStripeProduct => s.start_at === undefined,
        )
        const upcomingProduct = products.find(
            (s): s is UpcomingStripeProduct => s.start_at !== undefined,
        )
        return {
            ...state,
            stripeSubscriptionInfo: subscription_id
                ? {
                      subscriptionId: subscription_id,
                      activeProduct,
                      upcomingProduct,
                  }
                : null,
        }
    }

    if (FetchStripeSubscriptionInfoFailed.match(action)) {
        return {
            ...state,
            stripeSubscriptionInfo: null,
        }
    }

    return state
}

export const storagePlanReducerMapObj = {
    storagePlan: storagePlanReducer,
}

export type StateWithStoragePlan = StateOfReducerMapObj<typeof storagePlanReducerMapObj>
